import React, { useState, useEffect, useRef, useCallback } from 'react';

interface DebouncedTextInputProps {
  value: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  className?: string;
  delay?: number;
  ariaLabel?: string;
}

export const DebouncedTextInput: React.FC<DebouncedTextInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
  delay = 500,
  ariaLabel,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync from parent when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(newValue || undefined);
    }, delay);
  }, [onChange, delay]);

  return (
    <input
      type="text"
      className={className}
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
};
